'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import type { ClimbEntry } from '@/lib/climbData';

import Icon from '@/components/ui/AppIcon';

interface RankingTableProps {
  entries: ClimbEntry[];
  searchQuery: string;
}

export default function RankingTable({ entries, searchQuery }: RankingTableProps) {
  const [sortBy, setSortBy] = useState<'rank' | 'time' | 'date' | 'wpkg'>('rank');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const filtered = entries.filter(
    (e) =>
      e.status === 'approved' &&
      (searchQuery === '' || e.userName.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortBy === 'rank' || sortBy === 'time') cmp = a.timeSeconds - b.timeSeconds;
    else if (sortBy === 'date') cmp = new Date(a.date).getTime() - new Date(b.date).getTime();
    else if (sortBy === 'wpkg') cmp = b.wattPerKg - a.wattPerKg;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const handleSort = (col: typeof sortBy) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: typeof sortBy }) => {
    if (sortBy !== col) return <Icon name="ChevronUpDownIcon" size={14} className="text-muted-foreground" />;
    return sortDir === 'asc'
      ? <Icon name="ChevronUpIcon" size={14} className="text-primary" />
      : <Icon name="ChevronDownIcon" size={14} className="text-primary" />;
  };

  if (sorted.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-16 text-center fade-in">
        <Icon name="TrophyIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">Nessun tempo trovato</h3>
        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
          {searchQuery
            ? `Nessun ciclista corrisponde a "${searchQuery}". Prova con un altro nome.`
            : 'Non ci sono ancora tempi approvati per questa salita. Importa il tuo tempo da Strava per primo!'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden card-glow fade-in">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider w-16">
                #
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">
                Ciclista
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider cursor-pointer hover:text-foreground select-none"
                onClick={() => handleSort('time')}
              >
                <span className="flex items-center gap-1">Tempo <SortIcon col="time" /></span>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">
                Distacco
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider cursor-pointer hover:text-foreground select-none"
                onClick={() => handleSort('wpkg')}
              >
                <span className="flex items-center gap-1">W/kg <SortIcon col="wpkg" /></span>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider cursor-pointer hover:text-foreground select-none"
                onClick={() => handleSort('date')}
              >
                <span className="flex items-center gap-1">Data <SortIcon col="date" /></span>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">
                Fonte
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">
                Strava
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((entry, idx) => {
              const rank = idx + 1;
              let rankClass = '';
              let rankBg = '';
              if (rank === 1) { rankClass = 'text-yellow-400'; rankBg = 'rank-gold'; }
              else if (rank === 2) { rankClass = 'text-gray-300'; rankBg = 'rank-silver'; }
              else if (rank === 3) { rankClass = 'text-orange-400'; rankBg = 'rank-bronze'; }

              return (
                <tr
                  key={`entry-row-${entry.id}`}
                  className="border-b border-border/50 row-hover transition-all"
                >
                  <td className="px-4 py-3.5">
                    <span
                      className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                        rank <= 3 ? rankBg : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {rank}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                        {entry.userAvatar}
                      </div>
                      <Link
                        href={`/member/${entry.userId}`}
                        className="font-semibold text-foreground hover:text-primary transition-colors"
                      >
                        {entry.userName}
                      </Link>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`font-mono-nums font-bold text-base ${rank === 1 ? 'text-accent' : 'text-foreground'}`}>
                      {entry.timeFormatted}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    {entry.gapFormatted === 'Leader' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-yellow-400">
                        <Icon name="TrophyIcon" size={12} />
                        Leader
                      </span>
                    ) : (
                      <span className="font-mono-nums text-sm text-muted-foreground">
                        {entry.gapFormatted}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="font-mono-nums text-sm text-foreground">{entry.wattPerKg.toFixed(1)}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-sm text-muted-foreground font-mono-nums">
                      {entry.date}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    {entry.importedFromStrava ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-400">
                        <span className="w-2 h-2 rounded-full bg-orange-400" />
                        Strava
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                        <span className="w-2 h-2 rounded-full bg-muted-foreground" />
                        Manuale
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <a
                      href={entry.stravaActivityUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-orange-500/10 text-orange-400 text-xs font-semibold hover:bg-orange-500/20 transition-colors"
                    >
                      <Icon name="ArrowTopRightOnSquareIcon" size={12} />
                      Vedi
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 border-t border-border flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {sorted.length} tempi approvati
        </p>
        <p className="text-xs text-muted-foreground">
          Aggiornato: 22 Mag 2026, 08:54
        </p>
      </div>
    </div>
  );
}